import React, { createContext, useCallback, useState, useContext } from "react";

import { useHistory } from "react-router-dom";

import api from "../services/api";

interface User {
  id: string;
  name: string;
  email: string;
  avatar_url: string;
  active: boolean;
  user_level: number;
  login_name: string;
  legacy_id: string;
  access_info: string;
}

interface ManagementData {
  usuario_logado: string;
  categoria: string;
  setor: string;
  permissaoiv: string;
  permissaov: string;
  altSenha: string;
  menu: string;
  nivelusuario: string;
  clienteid: string;
  clientenome: string;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem("@Jurisnet:token");
    const user = localStorage.getItem("@Jurisnet:user");

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(
    async ({ email, password }) => {
      const response = await api.post("sessions", {
        email,
        password,
      });

      const { token, user } = response.data;

      localStorage.setItem("@Jurisnet:token", token);
      localStorage.setItem("@Jurisnet:user", JSON.stringify(user));

      setData({ token, user });

      api.defaults.headers.authorization = `Bearer ${token}`;

      try {
        const { data: access } = await api.get<ManagementData>(
          "/profile/access"
        );

        localStorage.setItem("@Jurisnet:access", JSON.stringify(access));

        await api.patch("/users/patch/access/logininfo", {
          access_info: JSON.stringify(access),
          login_name: user.login_name,
        });
      } catch (erro) {
        localStorage.removeItem("@Jurisnet:token");
        localStorage.removeItem("@Jurisnet:user");
        localStorage.removeItem("@Jurisnet:access");
        setData({} as AuthState);
        delete api.defaults.headers.authorization;

        throw erro;
      }

      history.push("/");
    },
    [history]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("@Jurisnet:token");
    localStorage.removeItem("@Jurisnet:user");
    localStorage.removeItem("@Jurisnet:lawsuit");
    localStorage.removeItem("@Jurisnet:judgingbody");
    localStorage.removeItem("@Jurisnet:counter");
    sessionStorage.removeItem("@Jurisnet:access");
    localStorage.removeItem("@Jurisnet:access");

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem("@Jurisnet:user", JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [data.token]
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}

export { AuthProvider, useAuth };
